import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import {
  fetchCourseCertificate,
  fetchCourseQuizQuestions,
  fetchCourseQuizResult,
  fetchCourseV2Progress,
  getBadRequestMessage,
  getRejectionError,
  isAttemptDesyncError,
  isNotFoundError,
  isSurveyAlreadySettled,
  postCourseQuizAnswer,
  postCourseQuizQuestionDisplayed,
  postCourseQuizResult,
  postCourseQuizSession,
  postCourseRestart,
  postCourseSurvey,
  postShowNotesStepCompleted,
} from 'src/v3/classroom-v2/utils/api'
import {
  ClassroomV2RejectionError,
  CourseV2CertificatePoll,
  CourseV2Progress,
  CourseV2QuizAnswerResponse,
  CourseV2QuizAttempt,
  CourseV2QuizQuestion,
  CourseV2QuizResult,
  CourseV2SurveyAttempt,
  CourseV2SurveyFreeTextKey,
  CourseV2SurveyLikertKey,
  CourseV2SurveySubmission,
  CourseV2SurveySubmissionResponse,
  CourseV2SurveySubmitRejection,
  CourseV2WizardPhase,
} from 'src/v3/classroom-v2/types'

// --- Design notes: exploration mode, rehash gating, completion, restart ---
//
// Mirrored on the "Classroom v2" Notion engineering reference page.
//
// 1. Exploration mode gates whether ANY stream event reaches the server.
//    Per the backend's own design (see ClassroomV2 Progress.fs notes): "the
//    backend has no mode concept — free-roam means the FE simply never calls
//    the stream-event write endpoints." explorationModeByCourseID is the
//    single flag that decides that.
//
// 2. Rehash gating is a SEPARATE, independent criterion — even in compliance
//    mode, revisiting a step at or below the user's high-water mark must not
//    emit an event. Show-notes steps: binary, already-completed = rehash.
//    Video steps: compare against the step's max progress (seconds) — only
//    watching beyond that max counts. These two gates compose (AND), they
//    don't replace each other — exploration mode can be off while a
//    particular action is still a no-op rehash.
//
//    IMPLEMENTED, though not where this note originally predicted. It isn't a
//    gate the slice consults: the video half lives in use-video-compliance.ts
//    (which owns seek semantics, as anticipated) and the step-level half is
//    isVideoStepInReviewMode in course-player.tsx.
//
// 3. Full course completion was going to force the course into exploration mode
//    via the same explorationModeByCourseID flag. It deliberately DOESN'T, and
//    the reason is worth keeping: flipping that flag runs the mode-change
//    middleware, which wipes the course's progress — erasing the very completed
//    state that made the course complete. So completion is read-only derived
//    state (selectIsCourseFullyComplete) that the player uses as a rendering-
//    time review override, and the shared flag is never touched.
//
// 4. Restart is the way out of a completed cycle, and it IS a real domain event
//    (POST .../restart, appending a CourseStreamEvent Status=Restarted boundary)
//    rather than a client-side mode change. The boundary is what scopes
//    steps/quiz/survey/certificate on the progress GET, so once it moves, the
//    server's next projection describes a fresh attempt and every phase gate
//    follows from it with no special-casing. See restartCourse below.

// --- Why this is a separate slice from classroom-v2.slice.ts ---
//
// classroom-v2.slice.ts holds server-cacheable content (courseContent) that
// is fine to keep around across navigations — it's static, so re-fetching it
// on every wizard visit would be wasted work (see index.tsx's uninitialized
// guard).
//
// Everything in THIS slice is scoped to a single wizard visit and must never
// be treated as cacheable, including the server's own progress snapshot:
// courseProgress is refetched fresh every time the wizard is entered (no
// "already fetched" guard, unlike courseContent), because progress can have
// changed anywhere else (another device, another tab) since the last visit.
// sessionProgress is a second, locally-advanced mirror of that snapshot used
// for optimistic UI (eventual consistency) as the user completes steps this
// session, without waiting on a round trip. resetWizard clears both plus
// exploration mode and the mark-complete request status, and must be
// dispatched on every wizard mount so a stale visit never leaks into a new
// one.

export let fetchCourseProgress = createAsyncThunk(
  'classroomV2Wizard/fetchCourseProgress',
  (courseID: string) => fetchCourseV2Progress(courseID),
)

export let markShowNotesStepCompleted = createAsyncThunk(
  'classroomV2Wizard/markShowNotesStepCompleted',
  (payload: { courseID: string; stepIndex: number; showNotesID: string }) =>
    postShowNotesStepCompleted(payload.courseID, payload.showNotesID).then(
      () => payload,
    ),
)

// Opens the quiz session, then reports the courseID so the middleware can
// refetch progress. The thunk deliberately does NOT return the session ID as
// something the UI acts on: the phase advances because the server's progress
// record now says inProgress, not because this POST resolved. That keeps the
// one rule this wizard is built around — the server decides what you've
// earned — true even for the transition the user just paid for.
//
// Reuse-not-create, so this is safe on every entry and needs no "does a session
// already exist" guard. It's also the only quiz endpoint that enforces the
// prior-steps gate, which makes it the server-side twin of selectWizardPhase's
// preQuiz check: even if something got a user to the pre-quiz screen without
// finishing the content, this 409s with PriorStepsNotCompleted and no session
// is created.
export let startQuizSession = createAsyncThunk(
  'classroomV2Wizard/startQuizSession',
  (courseID: string) => postCourseQuizSession(courseID).then(() => courseID),
)

// Loads the attempt. The session POST is repeated here on purpose rather than
// relying on the one the pre-quiz CTA fired: it's reuse-not-create, so on a
// reload straight into the quiz phase (progress said inProgress, no CTA was
// ever clicked) this is the only thing that hands back the session ID. Same
// call, same result, one entry path.
//
// The questions response is state-dependent, so the order matters — the session
// must exist before asking what to display.
export let loadQuizAttempt = createAsyncThunk(
  'classroomV2Wizard/loadQuizAttempt',
  (courseID: string) =>
    postCourseQuizSession(courseID).then((sessionID) =>
      fetchCourseQuizQuestions(courseID).then((questions) => ({
        sessionID,
        questions,
      })),
    ),
)

// The three quiz writes below all share one failure mode worth separating from
// the rest: a 409 saying this client's picture of the attempt no longer matches
// the server's (see isAttemptDesyncError). Those are surfaced through
// rejectWithValue so the rejected reducers can switch on the code and the
// middleware can trigger a resync, rather than every caller re-deriving that
// from an AxiosError. Any other failure rejects normally with the error itself,
// which is the retryable path.
type QuizWriteRejection = { rejectValue: ClassroomV2RejectionError }

let rejectDesync = <T>(
  error: unknown,
  rejectWithValue: (value: ClassroomV2RejectionError) => T,
): T | Promise<never> => {
  let rejection = getRejectionError(error)
  return rejection && isAttemptDesyncError(rejection.error)
    ? rejectWithValue(rejection.error)
    : Promise.reject(error)
}

// Records the answer and gets the explanation back. The response is the only
// source of the answer key, so this is also what unlocks the feedback panel.
//
// QuizQuestionAlreadyAnswered here means the server already holds an answer for
// this question — the user's answer is not lost, this client just doesn't know
// what it was, and the only thing that can tell it is a re-read.
export let submitQuizAnswer = createAsyncThunk<
  CourseV2QuizAnswerResponse,
  { courseID: string; sessionID: string; questionID: string; answerID: string },
  QuizWriteRejection
>('classroomV2Wizard/submitQuizAnswer', (payload, { rejectWithValue }) =>
  postCourseQuizAnswer(
    payload.courseID,
    payload.sessionID,
    payload.questionID,
    payload.answerID,
  ).catch((error) => rejectDesync(error, rejectWithValue)),
)

// Locks the next question into position server-side BEFORE advancing the local
// index, so a reload can't shuffle away the question the user is looking at.
// The advance is the fulfilled reducer's job — if this POST fails, the user
// stays put rather than moving to a question the server hasn't pinned.
//
// QuizQuestionNotInQuiz here means the question set this client is paging
// through isn't the one the server has for this quiz, so advancing to any index
// in it is meaningless — the list itself has to be replaced.
export let advanceToQuizQuestion = createAsyncThunk<
  { courseID: string; sessionID: string; questionID: string },
  { courseID: string; sessionID: string; questionID: string },
  QuizWriteRejection
>('classroomV2Wizard/advanceToQuizQuestion', (payload, { rejectWithValue }) =>
  postCourseQuizQuestionDisplayed(
    payload.courseID,
    payload.sessionID,
    payload.questionID,
  )
    .then(() => payload)
    .catch((error) => rejectDesync(error, rejectWithValue)),
)

// Scores the attempt. Idempotent server-side, so a retry after a dropped
// response returns the original score instead of writing a second result — but
// a session scored under a DIFFERENT session ID than the one this client holds
// still 409s with QuizSessionAlreadyScored, and the resync is what recovers the
// real result.
export let scoreQuizAttempt = createAsyncThunk<
  { courseID: string; result: CourseV2QuizResult },
  { courseID: string; sessionID: string },
  QuizWriteRejection
>('classroomV2Wizard/scoreQuizAttempt', (payload, { rejectWithValue }) =>
  postCourseQuizResult(payload.courseID, payload.sessionID)
    .then((result) => ({ courseID: payload.courseID, result }))
    .catch((error) => rejectDesync(error, rejectWithValue)),
)

// Submits the post-course survey. Append-only server-side, so there is no
// update path and no "does one already exist" check — a repeat POST 409s with
// SurveyAlreadySubmitted, which isSurveyAlreadySettled treats as "already past
// this step" rather than a failure.
//
// Like every other write in this wizard, the response does NOT drive the phase.
// The response's nextStep is always claimCertificate and could advance the user
// directly, but the middleware refetches progress and selectWizardPhase moves
// them from the server's own record instead — so a survey the server didn't
// record advances nobody, exactly as with the quiz.
//
// Every failure mode is classified HERE, at the boundary, into
// CourseV2SurveySubmitRejection — so the rejected reducer switches on a union
// instead of re-interrogating an AxiosError, and the middleware can ask the same
// question the same way. See that type for what each case means.
export let submitSurvey = createAsyncThunk<
  { courseID: string; response: CourseV2SurveySubmissionResponse },
  { courseID: string; submission: CourseV2SurveySubmission },
  { rejectValue: CourseV2SurveySubmitRejection }
>('classroomV2Wizard/submitSurvey', (payload, { rejectWithValue }) =>
  postCourseSurvey(payload.courseID, payload.submission)
    .then((response) => ({ courseID: payload.courseID, response }))
    .catch((error) => {
      let rejection = getRejectionError(error)
      if (rejection) return rejectWithValue({ kind: 'gate', rejection })
      let badRequest = getBadRequestMessage(error)
      return rejectWithValue(
        badRequest === null
          ? { kind: 'unknown' }
          : { kind: 'validation', message: badRequest },
      )
    }),
)

// Starts the course over: the server appends a new restart boundary, and
// everything before it stops counting.
//
// Reports the courseID rather than the response so nothing downstream can be
// tempted to read restartedTime. The boundary is the server's to own, and this
// client's interest in the restart is entirely "the old projection is void" —
// which is a fact, not a timestamp. The fulfilled reducer wipes the visit's
// state on that fact and the middleware refetches; the phase gate then derives a
// fresh attempt from the server's own new record, the same as it does on mount.
//
// No 409 to handle and no idempotency concern in the usual sense: repeat calls
// each append another boundary, which is harmless (a second restart of a course
// already at step 0 changes nothing observable) but not free, so the UI disables
// its trigger while this is pending rather than relying on that.
export let restartCourse = createAsyncThunk(
  'classroomV2Wizard/restartCourse',
  (courseID: string) => postCourseRestart(courseID).then(() => courseID),
)

// One poll of the certificate endpoint. Deliberately a plain thunk with no
// retry, backoff, or loop of its own: the CADENCE lives in the middleware (see
// pollCertificate), so this stays a single question asked once, and the reducers
// below only ever record one answer. Splitting it that way is what lets the loop
// be cancelled between requests without leaving a half-finished retry sequence
// running inside a thunk nobody is listening to any more.
export let pollCourseCertificate = createAsyncThunk(
  'classroomV2Wizard/pollCourseCertificate',
  (courseID: string) => fetchCourseCertificate(courseID),
)

// Re-reads the attempt from the server after a desync 409 and rebuilds the
// local attempt from whatever it finds — the recovery for every case in
// isAttemptDesyncError. It asks about the RESULT first because that's the case
// loadQuizAttempt can't represent: for a scored attempt the questions endpoint
// returns every question with no isCurrent, which would render as "submit now"
// on an attempt that is already closed. A 404 there is the normal "no scored
// attempt yet" answer, and only then does the unscored path apply.
type QuizAttemptResync =
  | { kind: 'scored'; result: CourseV2QuizResult }
  | { kind: 'unscored'; sessionID: string; questions: CourseV2QuizQuestion[] }

export let resyncQuizAttempt = createAsyncThunk<QuizAttemptResync, string>(
  'classroomV2Wizard/resyncQuizAttempt',
  (courseID) =>
    fetchCourseQuizResult(courseID)
      .then((result): QuizAttemptResync => ({ kind: 'scored', result }))
      .catch((error) =>
        isNotFoundError(error)
          ? postCourseQuizSession(courseID).then((sessionID) =>
              fetchCourseQuizQuestions(courseID).then(
                (questions): QuizAttemptResync => ({
                  kind: 'unscored',
                  sessionID,
                  questions,
                }),
              ),
            )
          : Promise.reject(error),
      ),
)

type ClassroomV2WizardState = {
  // Read-only replica of the last server response. Only ever replaced
  // wholesale by fetchCourseProgress — never mutated by local actions.
  courseProgressStatus: Record<string, Status>
  // The requestId of the most recently DISPATCHED fetchCourseProgress for each
  // course — not the most recently RESOLVED one. This wizard fires that thunk
  // from half a dozen places (mount, mode flips, every quiz/survey write,
  // restart), so several can be in flight at once, and network timing gives no
  // guarantee they resolve in the order they were sent. Without this guard,
  // fetchCourseProgress.fulfilled just overwrites courseProgress/sessionProgress
  // with whatever came back last in TIME — so a slow mount-time fetch that
  // resolves after a same-session quiz score clobbers the fresh "passed" record
  // with the stale pre-attempt one, and selectWizardPhase sends the learner back
  // to preQuiz having just passed. Comparing action.meta.requestId against this
  // on arrival is what makes "last dispatched" win instead of "last resolved".
  latestProgressRequestID: Record<string, string>
  courseProgress: Record<string, CourseV2Progress>
  // Seeded from the same hydration as courseProgress, then advanced locally
  // as the user completes steps this session — eagerly matching what the
  // server would eventually project, without waiting on a refetch. This is
  // what the UI renders from going forward.
  sessionProgress: Record<string, CourseV2Progress>
  explorationModeByCourseID: Record<string, boolean>
  markCompleteStatus: Record<string, Status>
  // In-memory only — never a route param. Deep-linking directly to a step
  // would let a user bypass earlier steps, which is a compliance threat
  // vector this wizard intentionally avoids by having no per-step URL.
  // Defaulting to undefined (via the selector) rather than storing null
  // means switching courseID naturally starts fresh with no extra reset.
  activeStepIndexByCourseID: Record<string, number>
  // Set by the course overview's "Review Course" press — a course that's
  // already fully complete has nothing left to earn, so reopening the player
  // should land it in free-roam rather than the compliance default. A
  // one-shot request rather than setting explorationModeByCourseID directly
  // FROM the overview page, because resetWizard (the player's own mount
  // effect) always wipes that flag back to the compliance default on entry —
  // setting it a page earlier would just be undone the instant the player
  // mounted. resetWizard is what consumes this, in the same reducer that
  // does the wiping, so the two can't disagree about which one wins. Never
  // read directly by a selector: nothing outside resetWizard has a reason to
  // know a request is pending.
  pendingExplorationEntryByCourseID: Record<string, boolean>
  // "Review course content first" from the pre-quiz screen. Deliberately
  // one-way: it can send you BACK to the content phase, never forward.
  // selectWizardPhase consults it only after confirming the server says every
  // step is complete, so setting it can't unlock anything — worst case a user
  // who has finished the content looks at that content again.
  isReviewingContentByCourseID: Record<string, boolean>
  // Set the instant a step completion (video finished, show notes marked
  // read) flips progress.allStepsCompleted from false to true THIS visit —
  // see markStepIndexCompleted. Without it, finishing the last step's content
  // requirement was indistinguishable from having finished it on some earlier
  // visit, and selectWizardPhase moves straight to preQuiz the moment
  // allStepsCompleted is true with no user action in between: the last
  // step's video ending or its "mark complete" press bounced the learner into
  // the pre-quiz screen on its own, with no "Continue to Assessment" press to
  // blame it on.
  //
  // Cleared by the same reviewContentFinished action the "Continue to
  // Assessment" affordances already dispatch — so pressing that button is
  // simultaneously "I'm done reviewing" (the pre-existing meaning) and "I'm
  // ready to move on from just finishing" (this one), and no second flag or
  // button is needed to tell those apart.
  //
  // Deliberately NOT set by the initial progress hydration: a returning user
  // whose steps were already complete before this visit started should land
  // straight on preQuiz, the same as today — only a completion that happens
  // live, during this visit, needs the extra click.
  justCompletedContentByCourseID: Record<string, boolean>
  // Request status for the create-session POST, so the pre-quiz screen can
  // disable its CTA and surface a failure. Not a phase — the phase still
  // comes from the refetched progress record.
  startQuizStatus: Record<string, Status>
  // The live attempt, absent until the quiz phase loads one. Visit-scoped like
  // everything else here: a stale attempt must never outlive a mode flip or a
  // remount, or a user could be shown answers and explanations from a session
  // the server has since moved past.
  quizAttemptByCourseID: Record<string, CourseV2QuizAttempt>
  // The live survey submission, absent until the survey surface mounts one.
  // Visit-scoped like the quiz attempt: a stale 'submitted' or
  // 'prerequisitesNotMet' must never outlive a remount, or a returning user
  // would be shown a verdict about a submission the server has since moved
  // past.
  surveyAttemptByCourseID: Record<string, CourseV2SurveyAttempt>
  // What the certificate poll has found, absent until the certificate surface
  // starts one. Visit-scoped for a sharper reason than the others: the issued
  // arm holds a presigned downloadURL that expires in minutes, so this record is
  // not merely stale after a remount, it is actively broken — a link from it
  // would 403. resetWizard dropping it is what guarantees a returning user
  // re-polls for a fresh one rather than being handed a dead link.
  certificatePollByCourseID: Record<string, CourseV2CertificatePoll>
  // Request status for the restart POST, so the surfaces offering it can disable
  // their trigger and report a failure.
  //
  // Deliberately NOT part of clearVisitScopedState, even though restart's own
  // fulfilled reducer calls that: a status wiped by the transition it describes
  // could never be read, and 'pending' in particular has to survive from the
  // press until the fresh progress lands or the whole affordance flickers back to
  // enabled mid-restart. resetWizard clears it, since a status from a previous
  // visit means nothing to this one.
  restartStatus: Record<string, Status>
}

export let initialState: ClassroomV2WizardState = {
  courseProgressStatus: {},
  latestProgressRequestID: {},
  courseProgress: {},
  sessionProgress: {},
  explorationModeByCourseID: {},
  markCompleteStatus: {},
  activeStepIndexByCourseID: {},
  pendingExplorationEntryByCourseID: {},
  isReviewingContentByCourseID: {},
  justCompletedContentByCourseID: {},
  startQuizStatus: {},
  quizAttemptByCourseID: {},
  surveyAttemptByCourseID: {},
  certificatePollByCourseID: {},
  restartStatus: {},
}

// Every piece of visit-scoped state for one course, wiped together. Both
// entry points into this slice's "start from nothing" transitions —
// resetWizard on mount and setExplorationMode on a mode flip — go through
// this, so there is exactly one definition of what a clean slate is and no
// way to wipe a subset of it. Exploration mode itself is deliberately NOT
// cleared here: resetWizard drops it separately (returning to the compliance
// default), while setExplorationMode sets it immediately after wiping.
let clearVisitScopedState = (
  state: ClassroomV2WizardState,
  courseID: string,
) => {
  // eslint-disable-next-line fp/no-delete
  delete state.courseProgressStatus[courseID]
  // eslint-disable-next-line fp/no-delete
  delete state.latestProgressRequestID[courseID]
  // eslint-disable-next-line fp/no-delete
  delete state.courseProgress[courseID]
  // eslint-disable-next-line fp/no-delete
  delete state.sessionProgress[courseID]
  // eslint-disable-next-line fp/no-delete
  delete state.markCompleteStatus[courseID]
  // eslint-disable-next-line fp/no-delete
  delete state.activeStepIndexByCourseID[courseID]
  // eslint-disable-next-line fp/no-delete
  delete state.isReviewingContentByCourseID[courseID]
  // eslint-disable-next-line fp/no-delete
  delete state.justCompletedContentByCourseID[courseID]
  // eslint-disable-next-line fp/no-delete
  delete state.startQuizStatus[courseID]
  // eslint-disable-next-line fp/no-delete
  delete state.quizAttemptByCourseID[courseID]
  // eslint-disable-next-line fp/no-delete
  delete state.surveyAttemptByCourseID[courseID]
  // eslint-disable-next-line fp/no-delete
  delete state.certificatePollByCourseID[courseID]
}

// A blank draft: every question unanswered. Nulls are what the wire accepts, but
// a draft in this state is NOT submittable — all thirteen are required, and the
// surveySubmitAttempted listener is what refuses to post an incomplete one.
// Spelled out field by field rather than built from
// Object.fromEntries so it stays assignable to CourseV2SurveySubmission — a field
// added to the contract fails to compile here until it's seeded.
let emptySurveyDraft: CourseV2SurveySubmission = {
  likertContentRelevantToPractice: null,
  likertLearningObjectivesMet: null,
  likertContentWellOrganized: null,
  likertVisualsSupportedLearning: null,
  likertVisualDesignEnhancedLearning: null,
  likertPresentationErrorFree: null,
  likertPresenterEngaging: null,
  likertPresenterKnowledgeable: null,
  likertNoBiasOrCommercialism: null,
  likertWillImpactPractice: null,
  freeTextMostEffectiveAspects: null,
  freeTextAreasForImprovement: null,
  freeTextFutureTopicRequests: null,
}

// The attempt state a session ID plus a question list implies. Shared by the
// initial load and the post-desync resync so those two can't disagree about
// what an unscored attempt looks like.
//
// No isCurrent means everything is answered and the attempt just isn't scored
// yet. That's the documented "submit now" state, not bad data — so it gets its
// own case rather than being coerced to index 0, which would re-show an
// already-answered question as if it were live.
let unscoredAttempt = (
  sessionID: string,
  // Array order IS display order — never re-sorted by sequence, because for a
  // randomized quiz the array is the server's shuffle.
  questions: CourseV2QuizQuestion[],
): CourseV2QuizAttempt => {
  let currentIndex = questions.findIndex((q) => q.isCurrent)
  return currentIndex === -1
    ? { kind: 'submitNow', sessionID, isAdvancing: false }
    : {
        kind: 'answering',
        sessionID,
        questions,
        currentIndex,
        selectedAnswerID: null,
        feedback: null,
        isSubmittingAnswer: false,
        isAdvancing: false,
        answerError: null,
      }
}

// Marks one step complete on the local progress mirror and reports whether
// THIS call is what finished the course — i.e. allStepsCompleted was false
// going in and every step (including this one) is done coming out. Shared by
// videoStepCompleted and markShowNotesStepCompleted.fulfilled so the two
// can't compute "did this just finish the course" two different ways, which
// is exactly the distinction justCompletedContentByCourseID needs: it must
// fire on the transition, never on a step completion that finds every OTHER
// step already done from a prior visit.
let markStepIndexCompleted = (
  progress: CourseV2Progress,
  stepIndex: number,
): boolean => {
  let wasIncomplete = !progress.allStepsCompleted
  progress.steps = progress.steps.map((step) =>
    step.stepIndex === stepIndex ? { ...step, completed: true } : step,
  )
  progress.allStepsCompleted = progress.steps.every((s) => s.completed)
  return wasIncomplete && progress.allStepsCompleted
}

// Folds a freshly scored quiz result into the LOCAL progress mirror instead of
// waiting on a round trip to /progress. scoreQuizAttempt.fulfilled and
// resyncQuizAttempt's 'scored' case both hand this the exact same shape the
// server would otherwise be asked to confirm — result.result and nextStep are
// the server's own verdict on THIS attempt, not a client guess — so re-fetching
// progress just to learn what this function already has in hand is the
// unneeded round trip this exists to remove.
//
// survey is only touched on a pass, and only in the two directions the result
// can actually justify: nextStep 'submitSurvey' means one is outstanding
// (unreachable before a pass, so this can only be opening it, never
// re-opening one already submitted); 'claimCertificate' means none is, which
// — because a required survey is gated on quizNotPassed and this client could
// not have submitted one before this pass — can only mean it was never
// required. Leaves 'submitted' alone regardless, since nothing about passing
// again could un-submit one.
let applyQuizResultToProgress = (
  progress: CourseV2Progress,
  result: CourseV2QuizResult,
): void => {
  progress.quiz = result.result.passed
    ? { kind: 'passed' }
    : {
        kind: 'failed',
        score: result.result.score,
        passingScore: result.result.passingScore,
      }
  progress.nextStep = result.nextStep
  if (!result.result.passed) return
  if (result.nextStep?.kind === 'submitSurvey') {
    progress.survey = { kind: 'notSubmitted' }
  } else if (
    result.nextStep?.kind === 'claimCertificate' &&
    progress.survey.kind !== 'submitted'
  ) {
    progress.survey = { kind: 'notRequired' }
  }
}

// Whether compliance mode permits seating the user on this step: it must be
// one they've already completed, or the first one they haven't. Derived from
// sessionProgress — the same source the stepper renders its locks from, so
// the guard and the UI can't disagree.
//
// This slice has no access to course content (that's the other slice), so
// it can't know the total step count. It doesn't need to: progress carries
// one entry per step, and any index at or below the first incomplete one is
// permitted. An index beyond the end of the course is harmless — the player
// resolves it to no step and renders the empty state, and it can't be
// reached through the stepper at all.
//
// With no progress yet (never hydrated, or the fetch rejected) only step 0
// is reachable, which is exactly what the middleware's landing logic falls
// back to in that case.
let isStepReachable = (
  state: ClassroomV2WizardState,
  courseID: string,
  stepIndex: number,
) => {
  let steps = state.sessionProgress[courseID]?.steps ?? []
  let completedIndices = steps
    .filter((s) => s.completed)
    .map((s) => s.stepIndex)
  if (completedIndices.includes(stepIndex)) return true
  let firstIncomplete = Array.from(
    { length: completedIndices.length + 1 },
    (_, i) => i,
  ).find((i) => !completedIndices.includes(i))
  return stepIndex <= (firstIncomplete ?? 0)
}

export let classroomV2WizardSlice = createSlice({
  name: 'classroomV2Wizard',
  initialState,
  reducers: {
    resetWizard(state, action: { payload: string }) {
      let courseID = action.payload
      clearVisitScopedState(state, courseID)
      // Consumed here, before the compliance default below is established, so
      // a "Review Course" press that arrived a page earlier is what decides
      // this visit's mode rather than losing to the reset it's surviving —
      // see pendingExplorationEntryByCourseID.
      let enterExploration = state.pendingExplorationEntryByCourseID[courseID]
      // eslint-disable-next-line fp/no-delete
      delete state.pendingExplorationEntryByCourseID[courseID]
      // eslint-disable-next-line fp/no-delete
      delete state.explorationModeByCourseID[courseID]
      if (enterExploration) {
        state.explorationModeByCourseID[courseID] = true
      }
      // Not in clearVisitScopedState (see restartStatus's definition), so it's
      // dropped explicitly here — a restart's outcome belongs to the visit that
      // asked for it.
      // eslint-disable-next-line fp/no-delete
      delete state.restartStatus[courseID]
    },
    // The one-shot request resetWizard consumes above. Dispatched from the
    // course overview, a page and a navigation before the reset it's aimed
    // at, so it has to survive as its own piece of state rather than as a
    // direct mode flip.
    explorationEntryRequested(state, action: { payload: string }) {
      state.pendingExplorationEntryByCourseID[action.payload] = true
    },
    setActiveStepIndex(
      state,
      action: { payload: { courseID: string; stepIndex: number } },
    ) {
      let { courseID, stepIndex } = action.payload
      // The step-lock rule is enforced HERE, not only in the stepper's
      // disabled state. A disabled button is a UX affordance; this is the
      // safeguard. Any dispatch from anywhere — a future component, a
      // listener, a replayed action — that tries to jump past the first
      // incomplete step in compliance mode is a no-op, so there is no code
      // path that can seat a user on a step their progress doesn't justify.
      if (
        !state.explorationModeByCourseID[courseID] &&
        !isStepReachable(state, courseID, stepIndex)
      ) {
        return
      }
      state.activeStepIndexByCourseID[courseID] = stepIndex
      // A failed completion POST shouldn't linger once the user moves to a
      // different step — each step gets its own attempt.
      // eslint-disable-next-line fp/no-delete
      delete state.markCompleteStatus[courseID]
    },
    // Switching modes is a full reset of the course's visit-scoped state,
    // performed in the SAME transition that flips the flag — active step
    // included. That atomicity is the whole point of it living here rather
    // than as a sequence of dispatches from the middleware: a user who
    // wanders to step 7 in exploration mode and flips back to compliance
    // must not remain on step 7, since their recorded progress may only
    // justify step 2. With a multi-dispatch wipe there is an observable
    // state between dispatches where the mode has changed but the step
    // hasn't been cleared — the stepper would render compliance locks
    // against a step index that outruns them, and any component reading
    // both in one render sees an inconsistent pair. One reducer, no window.
    //
    // Landing on a legitimate step afterwards is then the normal derived-
    // state path: activeStepIndex is null, so once the middleware's fresh
    // progress hydration settles, tryLandOnFirstIncompleteStep picks the
    // first incomplete step exactly as it does on mount. Entering
    // exploration mode wipes for the mirror-image reason — compliance
    // progress must not carry into a session that records nothing.
    setExplorationMode(
      state,
      action: { payload: { courseID: string; enabled: boolean } },
    ) {
      let { courseID, enabled } = action.payload
      clearVisitScopedState(state, courseID)
      state.explorationModeByCourseID[courseID] = enabled
    },
    // "Review course content first" from the pre-quiz screen. Safe to accept
    // unconditionally: it can only move the user backwards into content they
    // have already completed, and selectWizardPhase ignores it unless the
    // server says every step is done. Sends them to index 0 so review starts
    // at the first step; every step is already reachable by definition, so the
    // setActiveStepIndex lock has nothing to object to.
    reviewContentRequested(state, action: { payload: string }) {
      state.isReviewingContentByCourseID[action.payload] = true
      state.activeStepIndexByCourseID[action.payload] = 0
    },
    // Leaving review returns the user to whatever phase their progress
    // actually puts them in — which is computed, not stored, so this just
    // drops the flag rather than naming a destination.
    reviewContentFinished(state, action: { payload: string }) {
      // eslint-disable-next-line fp/no-delete
      delete state.isReviewingContentByCourseID[action.payload]
      // eslint-disable-next-line fp/no-delete
      delete state.justCompletedContentByCourseID[action.payload]
    },
    // The user has read their score and is moving on. Dropping the attempt
    // releases selectWizardPhase's hold on the quiz phase, so the phase falls
    // back to whatever the server's progress record says — survey or certificate
    // for a pass, preQuiz for a fail. Like reviewContentFinished, this names no
    // destination: it removes a reason to stay, and the derived phase does the
    // rest. That's what keeps the server the only thing that decides where a user
    // is allowed to go next.
    quizResultAcknowledged(state, action: { payload: string }) {
      let attempt = state.quizAttemptByCourseID[action.payload]
      // Only a scored attempt can be acknowledged. Guarding the kind means a
      // stray dispatch mid-attempt can't discard answered questions and drop the
      // user back to the pre-quiz screen.
      if (attempt?.kind !== 'scored') return
      // eslint-disable-next-line fp/no-delete
      delete state.quizAttemptByCourseID[action.payload]
    },
    // There are deliberately no per-field clear reducers here (progress,
    // session progress, and mark-complete status each used to have one).
    // Wiping one slice of a course's visit-scoped state without the others
    // is never correct — it's what let a mode flip drop progress while
    // leaving the active step pointing past it. resetWizard and
    // setExplorationMode are the only ways to clear, and both wipe
    // everything at once via clearVisitScopedState.
    // Fact announcements from useVideoCompliance — the progress/completion
    // POST has already succeeded by the time these are dispatched, so
    // there's nothing to await here, just recording what happened.
    videoProgressMade(
      state,
      action: {
        payload: {
          courseID: string
          stepIndex: number
          progressSeconds: number
        }
      },
    ) {
      let { courseID, stepIndex, progressSeconds } = action.payload
      let progress = state.sessionProgress[courseID]
      if (!progress) return
      // progressSeconds is a high-water mark, same as the server's own
      // semantics (see CourseV2VideoStepProgress's definition) — never
      // regress it, since out-of-order posts or a late-resolving throttled
      // call could otherwise overwrite a further-along position with an
      // earlier one.
      progress.steps = progress.steps.map((step) =>
        step.kind === 'videoStep' && step.stepIndex === stepIndex
          ? {
              ...step,
              progressSeconds: Math.max(
                step.progressSeconds ?? 0,
                progressSeconds,
              ),
            }
          : step,
      )
    },
    // Staging a choice, not recording one — nothing reaches the server until
    // submitQuizAnswer. Ignored once feedback is in, because at that point the
    // answer is already scored and there is nothing left to choose; letting a
    // late selection through would repaint the correct/incorrect highlighting
    // against an answer the user never actually gave.
    quizAnswerSelected(
      state,
      action: { payload: { courseID: string; answerID: string } },
    ) {
      let attempt = state.quizAttemptByCourseID[action.payload.courseID]
      if (attempt?.kind !== 'answering') return
      if (attempt.feedback || attempt.isSubmittingAnswer) return
      attempt.selectedAnswerID = action.payload.answerID
      attempt.answerError = null
    },
    // The survey surface announcing it's mounted with nothing in hand, which
    // seeds an empty draft. Not a fetch: the form starts blank because the survey
    // is append-only and a submitted one is never re-opened for editing, so there
    // is nothing to hydrate from.
    //
    // Idempotent on purpose — re-announcing must not wipe a draft, a submission
    // verdict, or a submit-in-flight. Without that guard a remount mid-request
    // would drop the user back to a blank form while the POST was still going,
    // and any re-render that dispatched this would silently erase their answers.
    surveyOpened(state, action: { payload: string }) {
      if (state.surveyAttemptByCourseID[action.payload]) return
      state.surveyAttemptByCourseID[action.payload] = {
        kind: 'editing',
        draft: emptySurveyDraft,
        hasAttemptedSubmit: false,
        submitError: null,
      }
    },
    // The learner pressed Submit. All thirteen questions are required, so this
    // does NOT mean "post it" — it means the learner has asked to, which is both
    // the cue to start showing what's missing and the trigger the middleware
    // checks completeness against before issuing submitSurvey. Announcing the
    // press and deciding what it causes are deliberately separate: the component
    // doesn't get to know the validation rule.
    surveySubmitAttempted(state, action: { payload: string }) {
      let attempt = state.surveyAttemptByCourseID[action.payload]
      if (attempt?.kind !== 'editing') return
      attempt.hasAttemptedSubmit = true
    },
    // One reducer for both question types — the field name is the discriminator
    // and the payload type follows from it, so a Likert key can't be handed a
    // string or vice versa.
    //
    // Ignored unless the attempt is still editing: once a submission is in
    // flight, submitted, or refused on gate grounds, the draft is no longer
    // something the learner is composing, and letting a late keystroke through
    // would edit answers the server has already been told about.
    surveyAnswerChanged(
      state,
      action: {
        payload: { courseID: string } & (
          | { name: CourseV2SurveyLikertKey; value: number }
          | { name: CourseV2SurveyFreeTextKey; value: string }
        )
      },
    ) {
      let attempt = state.surveyAttemptByCourseID[action.payload.courseID]
      if (attempt?.kind !== 'editing') return
      // A free-text field the learner has cleared is a SKIPPED question, not an
      // empty string — null is what the contract uses for "no answer", and
      // sending "" would record a blank response as if it were one.
      let { name, value } = action.payload
      attempt.draft = {
        ...attempt.draft,
        [name]: typeof value === 'string' && value.trim() === '' ? null : value,
      }
      // A new answer clears a stale failure message: the learner is acting on it,
      // and leaving it up next to changed answers would describe a request that
      // no longer matches what's on screen.
      attempt.submitError = null
    },
    // The certificate surface announcing it's showing, which is the cue for the
    // middleware to start polling. Not a fetch and not a state change — the
    // reducer seeds a 'polling' record so the surface has something to render
    // immediately, and the loop's cadence is none of its business.
    //
    // Also the retry path: the surface re-dispatches this after a failed poll,
    // and the middleware's cancelActiveListeners makes that a restart rather than
    // a second concurrent loop. Idempotent from the surface's point of view for
    // that reason.
    certificatePollRequested(state, action: { payload: string }) {
      let existing = state.certificatePollByCourseID[action.payload]
      state.certificatePollByCourseID[action.payload] = {
        kind: 'polling',
        last:
          existing?.kind === 'answered'
            ? existing.certificate
            : (existing?.last ?? null),
        attempts: 0,
      }
    },
    videoStepCompleted(
      state,
      action: { payload: { courseID: string; stepIndex: number } },
    ) {
      let { courseID, stepIndex } = action.payload
      let progress = state.sessionProgress[courseID]
      if (!progress) return
      if (markStepIndexCompleted(progress, stepIndex)) {
        state.justCompletedContentByCourseID[courseID] = true
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCourseProgress.pending, (state, action) => {
        state.courseProgressStatus[action.meta.arg] = 'pending'
        state.latestProgressRequestID[action.meta.arg] = action.meta.requestId
      })
      .addCase(fetchCourseProgress.rejected, (state, action) => {
        // An older, superseded request failing says nothing about the newer
        // one still in flight — same "last dispatched wins" rule as fulfilled
        // below, so this can't flip a fresh pending back to rejected.
        if (
          state.latestProgressRequestID[action.meta.arg] !==
          action.meta.requestId
        ) {
          return
        }
        state.courseProgressStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseProgress.fulfilled, (state, action) => {
        // Only the most recently DISPATCHED request may commit its result — see
        // latestProgressRequestID. Anything else is a straggler from an earlier
        // fetch that resolved late, and applying it would roll a fresher
        // record (e.g. a just-scored quiz) back to a stale one.
        if (
          state.latestProgressRequestID[action.meta.arg] !==
          action.meta.requestId
        ) {
          return
        }
        state.courseProgressStatus[action.meta.arg] = 'fulfilled'
        state.courseProgress[action.meta.arg] = action.payload
        state.sessionProgress[action.meta.arg] = action.payload
      })
      .addCase(startQuizSession.pending, (state, action) => {
        state.startQuizStatus[action.meta.arg] = 'pending'
        // Opening a session is the fact that invalidates any attempt this client
        // is holding, so the attempt is dropped HERE rather than left for the
        // quiz surface to notice. Without this, a retake after a failed attempt
        // remounts QuizStep on the still-'scored' attempt from the attempt just
        // finished: it renders the old fail screen, and its load effect (gated on
        // attempt === null) never fires, so the new session's questions are never
        // fetched. Try Again then re-POSTs a session that changes nothing
        // observable — the retake looks like it does nothing at all.
        //
        // Clearing on pending, not fulfilled, so there is no frame where a new
        // session is being opened while a stale result is still on screen.
        // eslint-disable-next-line fp/no-delete
        delete state.quizAttemptByCourseID[action.meta.arg]
      })
      .addCase(startQuizSession.rejected, (state, action) => {
        state.startQuizStatus[action.meta.arg] = 'rejected'
      })
      .addCase(startQuizSession.fulfilled, (state, action) => {
        state.startQuizStatus[action.payload] = 'fulfilled'
        // No progress refetch needed here. The quiz phase is already held —
        // see selectWizardPhase — by startQuizStatus being 'pending' a moment
        // ago and by the attempt QuizStep's own loadQuizAttempt is about to
        // populate; neither reads progress.quiz at all until the attempt is
        // acknowledged away. Fetching progress just to confirm "session
        // exists" is what raced QuizStep's own load of the same session in the
        // first place — see loadQuizAttempt's callers.
      })
      .addCase(loadQuizAttempt.pending, (state, action) => {
        state.quizAttemptByCourseID[action.meta.arg] = { kind: 'loading' }
        // The hold on the quiz phase transfers to quizAttemptByCourseID as of
        // THIS line, so startQuizStatus's job (see selectWizardPhase's
        // 'fulfilled' branch) is done — cleared here rather than left to
        // linger, so it can't still read 'fulfilled' long after this attempt is
        // gone and mistakenly hold the phase again post-acknowledgement. A
        // no-op on a cold reload, where nothing set it this visit.
        // eslint-disable-next-line fp/no-delete
        delete state.startQuizStatus[action.meta.arg]
      })
      .addCase(loadQuizAttempt.rejected, (state, action) => {
        state.quizAttemptByCourseID[action.meta.arg] = { kind: 'failedToLoad' }
      })
      .addCase(loadQuizAttempt.fulfilled, (state, action) => {
        let { sessionID, questions } = action.payload
        state.quizAttemptByCourseID[action.meta.arg] = unscoredAttempt(
          sessionID,
          questions,
        )
      })
      .addCase(submitQuizAnswer.pending, (state, action) => {
        let attempt = state.quizAttemptByCourseID[action.meta.arg.courseID]
        if (attempt?.kind !== 'answering') return
        attempt.isSubmittingAnswer = true
        attempt.answerError = null
      })
      .addCase(submitQuizAnswer.rejected, (state, action) => {
        let attempt = state.quizAttemptByCourseID[action.meta.arg.courseID]
        if (attempt?.kind !== 'answering') return
        attempt.isSubmittingAnswer = false
        // A desync 409 is not a retryable failure — re-posting sends the server
        // the same request it just refused on grounds that won't change, and for
        // QuizQuestionAlreadyAnswered the answer isn't even lost. Drop to
        // 'loading' so no stale question, staged choice, or Retry affordance is
        // on screen while the resync (dispatched by the middleware off this
        // action) fetches what the server actually holds.
        if (action.payload && isAttemptDesyncError(action.payload)) {
          state.quizAttemptByCourseID[action.meta.arg.courseID] = {
            kind: 'loading',
          }
          return
        }
        // No feedback is set here on purpose. A failed answer POST — including
        // the 500 for a question with no recorded correct answer, which the
        // server deliberately does NOT count as wrong — must not read as
        // "incorrect." The choice stays staged so the user can retry.
        attempt.answerError =
          'We could not record that answer. Please try again.'
      })
      .addCase(submitQuizAnswer.fulfilled, (state, action) => {
        let attempt = state.quizAttemptByCourseID[action.meta.arg.courseID]
        if (attempt?.kind !== 'answering') return
        attempt.isSubmittingAnswer = false
        attempt.feedback = action.payload
        // Pin the selection to the answer that was actually scored, so the
        // highlighting can't disagree with the server's record even if
        // something dispatched a selection while this was in flight.
        attempt.selectedAnswerID = action.meta.arg.answerID
      })
      .addCase(advanceToQuizQuestion.pending, (state, action) => {
        let attempt = state.quizAttemptByCourseID[action.meta.arg.courseID]
        if (attempt?.kind !== 'answering') return
        attempt.isAdvancing = true
      })
      .addCase(advanceToQuizQuestion.rejected, (state, action) => {
        let attempt = state.quizAttemptByCourseID[action.meta.arg.courseID]
        if (attempt?.kind !== 'answering') return
        // QuizQuestionNotInQuiz means the question list being paged through
        // isn't this quiz's, so there is no position in it worth holding — the
        // list has to be replaced, not retried against.
        if (action.payload && isAttemptDesyncError(action.payload)) {
          state.quizAttemptByCourseID[action.meta.arg.courseID] = {
            kind: 'loading',
          }
          return
        }
        // Stay on the current question with its feedback intact. Advancing
        // locally without the server having pinned the next question is exactly
        // what would let a reload swap it out.
        attempt.isAdvancing = false
      })
      .addCase(advanceToQuizQuestion.fulfilled, (state, action) => {
        let attempt = state.quizAttemptByCourseID[action.meta.arg.courseID]
        if (attempt?.kind !== 'answering') return
        // Advance to the question the POST actually pinned, found by ID rather
        // than by incrementing — the index and the pinned question can't drift
        // apart that way.
        let nextIndex = attempt.questions.findIndex(
          (q) => q.quizQuestion.id === action.meta.arg.questionID,
        )
        if (nextIndex === -1) return
        attempt.currentIndex = nextIndex
        attempt.selectedAnswerID = null
        attempt.feedback = null
        attempt.isAdvancing = false
        attempt.answerError = null
      })
      .addCase(scoreQuizAttempt.pending, (state, action) => {
        let attempt = state.quizAttemptByCourseID[action.meta.arg.courseID]
        if (attempt?.kind === 'answering' || attempt?.kind === 'submitNow') {
          attempt.isAdvancing = true
        }
      })
      .addCase(scoreQuizAttempt.rejected, (state, action) => {
        // QuizSessionAlreadyScored means a result already exists — the score
        // isn't lost, it's just held under a session this client isn't asking
        // about. The resync reads it back rather than offering a re-submit that
        // can only be refused again.
        if (action.payload && isAttemptDesyncError(action.payload)) {
          state.quizAttemptByCourseID[action.meta.arg.courseID] = {
            kind: 'loading',
          }
          return
        }
        let attempt = state.quizAttemptByCourseID[action.meta.arg.courseID]
        if (attempt?.kind === 'answering' || attempt?.kind === 'submitNow') {
          attempt.isAdvancing = false
        }
      })
      .addCase(scoreQuizAttempt.fulfilled, (state, action) => {
        state.quizAttemptByCourseID[action.payload.courseID] = {
          kind: 'scored',
          result: action.payload.result,
        }
        // The phase does NOT change here — quizResultAcknowledged is still the
        // only thing that releases the quiz phase's hold. What DOES happen is
        // folding this result into sessionProgress (see
        // applyQuizResultToProgress), so that once the phase falls through to
        // progress.quiz/survey it reads what this response just said rather
        // than a round trip's worth of history behind it.
        let progress = state.sessionProgress[action.payload.courseID]
        if (progress) applyQuizResultToProgress(progress, action.payload.result)
      })
      .addCase(resyncQuizAttempt.pending, (state, action) => {
        state.quizAttemptByCourseID[action.meta.arg] = { kind: 'loading' }
      })
      .addCase(resyncQuizAttempt.rejected, (state, action) => {
        // The desync is now compounded by not being able to read the truth
        // either, so the only honest thing to render is the failure surface with
        // its Retry — which re-enters through loadQuizAttempt and lands on a
        // fresh reuse-not-create session.
        state.quizAttemptByCourseID[action.meta.arg] = { kind: 'failedToLoad' }
      })
      .addCase(resyncQuizAttempt.fulfilled, (state, action) => {
        state.quizAttemptByCourseID[action.meta.arg] =
          action.payload.kind === 'scored'
            ? { kind: 'scored', result: action.payload.result }
            : unscoredAttempt(
                action.payload.sessionID,
                action.payload.questions,
              )
        // Same fold as scoreQuizAttempt.fulfilled — a resync that turns up a
        // scored result is the server's verdict on this attempt just as much as
        // scoring it directly was, so it updates sessionProgress the same way.
        if (action.payload.kind === 'scored') {
          let progress = state.sessionProgress[action.meta.arg]
          if (progress) {
            applyQuizResultToProgress(progress, action.payload.result)
          }
        }
      })
      // The draft carries over from meta.arg rather than being dropped: it's the
      // exact body being posted, so the form keeps rendering the learner's own
      // answers — disabled — while the request is in flight, instead of blanking
      // them at the moment they'd least want to lose them.
      .addCase(submitSurvey.pending, (state, action) => {
        state.surveyAttemptByCourseID[action.meta.arg.courseID] = {
          kind: 'submitting',
          draft: action.meta.arg.submission,
        }
      })
      .addCase(submitSurvey.rejected, (state, action) => {
        let { courseID } = action.meta.arg
        // Nothing in payload means the thunk threw before it could classify,
        // which is the same situation as 'unknown' — the submission didn't land
        // and retrying is the move.
        let rejection: CourseV2SurveySubmitRejection = action.payload ?? {
          kind: 'unknown',
        }
        if (rejection.kind === 'gate') {
          let { error } = rejection.rejection
          // Already submitted, or never required — the user is PAST this step,
          // so this is not a failure to show them. 'settled' renders as a brief
          // acknowledgement while the middleware's progress refetch moves the
          // phase on.
          if (isSurveyAlreadySettled(error)) {
            state.surveyAttemptByCourseID[courseID] = { kind: 'settled' }
            return
          }
          // A real refusal with work still owed. Narrowing on error is what
          // exposes unmetPrerequisites — see ClassroomV2RejectionResponse.
          if (rejection.rejection.error === 'SurveyPrerequisitesNotMet') {
            state.surveyAttemptByCourseID[courseID] = {
              kind: 'prerequisitesNotMet',
              unmetPrerequisites: rejection.rejection.unmetPrerequisites,
            }
            return
          }
        }
        // Retryable, so this drops back to editing with a message. The draft
        // comes from meta.arg — what was actually posted — so a failure never
        // costs the learner their answers, and the form they return to is the
        // form they submitted.
        state.surveyAttemptByCourseID[courseID] = {
          kind: 'editing',
          draft: action.meta.arg.submission,
          // Getting as far as a POST means they pressed Submit, so returning to
          // editing must not un-remember that.
          hasAttemptedSubmit: true,
          submitError:
            rejection.kind === 'validation'
              ? rejection.message
              : 'We could not submit your survey. Please try again.',
        }
      })
      .addCase(submitSurvey.fulfilled, (state, action) => {
        let { courseID, response } = action.payload
        state.surveyAttemptByCourseID[courseID] = {
          kind: 'submitted',
          surveyID: response.surveyID,
        }
        // Folded into sessionProgress rather than refetched — the surveyID this
        // response carries IS the server's record of the submission, so a round
        // trip to /progress would only be asking it to confirm what it just
        // said. nextStep (always claimCertificate here) isn't acted on for
        // navigation, same as scoreQuizAttempt: selectWizardPhase still derives
        // the phase from progress.survey, this just makes sure that read finds
        // 'submitted' instead of stale content.
        let progress = state.sessionProgress[courseID]
        if (progress) {
          progress.survey = { kind: 'submitted', surveyID: response.surveyID }
          progress.nextStep = response.nextStep
        }
      })
      // Each poll keeps whatever the last one found on screen rather than
      // blanking to a spinner — the pending screen's "waiting since" line must
      // survive a dozen requests, and a certificate that has just been issued
      // must not flicker back to generic loading on the next tick.
      .addCase(pollCourseCertificate.pending, (state, action) => {
        let existing = state.certificatePollByCourseID[action.meta.arg]
        state.certificatePollByCourseID[action.meta.arg] = {
          kind: 'polling',
          last:
            existing?.kind === 'answered'
              ? existing.certificate
              : (existing?.last ?? null),
          // Counted here rather than in the middleware so the surface can say
          // how long this has been going on without the loop having to tell it.
          // Carried across every case rather than only 'polling', so an answered
          // pending — the state the wait actually rests in between requests —
          // doesn't drop the count back to one and restart the clock.
          attempts: (existing?.attempts ?? 0) + 1,
        }
      })
      .addCase(pollCourseCertificate.rejected, (state, action) => {
        let existing = state.certificatePollByCourseID[action.meta.arg]
        // Not terminal-looking on purpose: failing to ASK says nothing about
        // whether a certificate exists. The last known state is carried so the
        // surface can keep rendering it alongside a retry.
        state.certificatePollByCourseID[action.meta.arg] = {
          kind: 'failed',
          last:
            existing?.kind === 'answered'
              ? existing.certificate
              : (existing?.last ?? null),
          attempts: existing?.attempts ?? 0,
        }
      })
      .addCase(pollCourseCertificate.fulfilled, (state, action) => {
        // 'answered' regardless of which of the three came back — including
        // pending, which is a complete answer to the question asked. Whether to
        // ask again is the middleware's decision, made off this same payload via
        // isCertificateResolved; the slice records what the server said and
        // nothing about the cadence, so one place knows when to stop.
        state.certificatePollByCourseID[action.meta.arg] = {
          kind: 'answered',
          certificate: action.payload,
          attempts:
            state.certificatePollByCourseID[action.meta.arg]?.attempts ?? 0,
        }
      })
      .addCase(restartCourse.pending, (state, action) => {
        state.restartStatus[action.meta.arg] = 'pending'
      })
      .addCase(restartCourse.rejected, (state, action) => {
        // Nothing is wiped on a failure. The 403 case means the restart didn't
        // happen, so the old projection is still the true one — dropping it would
        // strand the learner with no certificate on screen and no attempt either.
        state.restartStatus[action.meta.arg] = 'rejected'
      })
      .addCase(restartCourse.fulfilled, (state, action) => {
        let courseID = action.payload
        // The boundary has moved, so every cached thing about the old attempt is
        // now describing something that no longer counts — a passed quiz, a
        // submitted survey, an issued certificate with its presigned link. Wiping
        // them in the SAME transition that records the restart is the point, for
        // the same reason setExplorationMode wipes atomically: any window where
        // the restart is recorded but the old progress isn't cleared is a window
        // where a component reading both sees a course that is simultaneously
        // restarted and complete, and renders a certificate for an attempt the
        // server has voided.
        //
        // This is also why the contract bothers to return restartedTime — so a
        // client can drop its cache immediately instead of waiting on the GET.
        // The wipe is that, done without needing to compare timestamps to
        // anything.
        clearVisitScopedState(state, courseID)
        // Exploration mode is deliberately left alone. It describes how the
        // learner wants to work through the course, not what the server has
        // recorded, and a restart says nothing about that preference — unlike
        // resetWizard, which is a new visit and starts from the compliance
        // default. In practice restart is only offered from surfaces that exist
        // in compliance mode anyway.
        //
        // The middleware refetches progress off this action; activeStepIndex is
        // null after the wipe, so the landing logic seats the learner on step 1
        // exactly as it does on mount. No step, phase, or destination is named
        // here.
        state.restartStatus[courseID] = 'fulfilled'
      })
      .addCase(markShowNotesStepCompleted.pending, (state, action) => {
        state.markCompleteStatus[action.meta.arg.courseID] = 'pending'
      })
      .addCase(markShowNotesStepCompleted.rejected, (state, action) => {
        state.markCompleteStatus[action.meta.arg.courseID] = 'rejected'
      })
      .addCase(markShowNotesStepCompleted.fulfilled, (state, action) => {
        let { courseID, stepIndex } = action.payload
        state.markCompleteStatus[courseID] = 'fulfilled'
        let progress = state.sessionProgress[courseID]
        if (!progress) return
        if (markStepIndexCompleted(progress, stepIndex)) {
          state.justCompletedContentByCourseID[courseID] = true
        }
      })
  },
})

export let {
  certificatePollRequested,
  explorationEntryRequested,
  resetWizard,
  setActiveStepIndex,
  setExplorationMode,
  reviewContentRequested,
  reviewContentFinished,
  quizAnswerSelected,
  quizResultAcknowledged,
  surveyOpened,
  surveyAnswerChanged,
  surveySubmitAttempted,
  videoProgressMade,
  videoStepCompleted,
} = classroomV2WizardSlice.actions

export let selectCourseProgressByID = (id: string) => (state: AppRootState) =>
  state.classroomV2Wizard.courseProgress[id]
export let selectCourseProgressStatusByID =
  (id: string) => (state: AppRootState) =>
    state.classroomV2Wizard.courseProgressStatus[id] ?? 'uninitialized'
export let selectSessionProgressByID = (id: string) => (state: AppRootState) =>
  state.classroomV2Wizard.sessionProgress[id]
export let selectSessionCompletedStepIndicesByID =
  (id: string) => (state: AppRootState) =>
    state.classroomV2Wizard.sessionProgress[id]?.steps
      .filter((s) => s.completed)
      .map((s) => s.stepIndex) ?? []
export let selectExplorationModeByCourseID =
  (id: string) => (state: AppRootState) =>
    state.classroomV2Wizard.explorationModeByCourseID[id] ?? false
export let selectMarkCompleteStatusByID =
  (id: string) => (state: AppRootState) =>
    state.classroomV2Wizard.markCompleteStatus[id] ?? 'uninitialized'
export let selectActiveStepIndexByCourseID =
  (id: string) => (state: AppRootState) =>
    state.classroomV2Wizard.activeStepIndexByCourseID[id] ?? null
export let selectIsReviewingContentByCourseID =
  (id: string) => (state: AppRootState) =>
    state.classroomV2Wizard.isReviewingContentByCourseID[id] ?? false
export let selectStartQuizStatusByID = (id: string) => (state: AppRootState) =>
  state.classroomV2Wizard.startQuizStatus[id] ?? 'uninitialized'
// null means "no attempt loaded for this course yet" — the quiz surface reads
// that as its cue to dispatch loadQuizAttempt, which is why there's no separate
// uninitialized case inside the union.
export let selectQuizAttemptByCourseID =
  (id: string) =>
  (state: AppRootState): CourseV2QuizAttempt | null =>
    state.classroomV2Wizard.quizAttemptByCourseID[id] ?? null
// null means "the survey surface hasn't announced itself yet" — it reads that as
// its cue to dispatch surveyOpened, mirroring how the quiz surface treats a null
// attempt.
export let selectSurveyAttemptByCourseID =
  (id: string) =>
  (state: AppRootState): CourseV2SurveyAttempt | null =>
    state.classroomV2Wizard.surveyAttemptByCourseID[id] ?? null

// THE gate for what the wizard renders. Every phase transition is a
// consequence of the server's own progress record, so there is no action a
// user can dispatch and no URL they can type that seats them in a phase they
// haven't earned:
//
//   - preQuiz and beyond require progress.allStepsCompleted, which only the
//     server sets (from stream events it verified itself).
//   - quiz requires the server to have handed back an inProgress session ID.
//   - survey requires quiz.kind === 'passed'.
//   - certificate requires a passed quiz AND a survey that is either
//     submitted or genuinely not required.
//
// Note this gate decides what RENDERS, and nothing more. It never decides who
// has earned a certificate or a CE submission — the API owns issuing both, and
// a notRequired survey does not mean no certificate. Reaching the certificate
// phase is this selector reporting what the server already recorded, not the
// client conferring anything.
//
// Evaluated in order, so each phase implicitly depends on all the earlier
// gates having passed. Exploration mode short-circuits to content: free-roam
// reads no server progress at all, so it can never satisfy a gate, and a pure
// review surface has no business rendering a quiz or minting a certificate.
//
// Returns null while progress hasn't hydrated — the caller shows a skeleton
// rather than guessing a phase and flashing the wrong surface.
export let selectWizardPhase =
  (id: string) =>
  (state: AppRootState): CourseV2WizardPhase | null => {
    if (state.classroomV2Wizard.explorationModeByCourseID[id]) {
      return { kind: 'content' }
    }
    let progress = state.classroomV2Wizard.sessionProgress[id]
    if (!progress) return null
    if (!progress.allStepsCompleted) return { kind: 'content' }
    // Only meaningful once the content is done — checked after the gate above
    // so it can't be used to dodge one. justCompletedContentByCourseID is the
    // other reason to hold here: it's what stops the step that JUST finished
    // the course from also being the thing that leaves it, with no click in
    // between — see that field's own comment for why it only fires on this
    // visit's transition and not on progress that arrived already complete.
    if (
      state.classroomV2Wizard.isReviewingContentByCourseID[id] ||
      state.classroomV2Wizard.justCompletedContentByCourseID[id]
    ) {
      return { kind: 'content' }
    }
    // THE line between the two assessment surfaces, and the only one:
    //
    //   preQuiz — this client is holding no attempt and isn't opening one.
    //   quiz    — an attempt is open, being opened, or scored and unread.
    //
    // Everything the user reported as "flashing" was this question being asked
    // of the progress record instead, which cannot answer it. The record
    // describes the last attempt the SERVER finished, so it reads 'failed' for
    // the entire window between pressing Retake and the refetch landing, and it
    // reads 'failed'/'passed' the instant a result is written — while the user is
    // still reading their score. Deriving the surface from that meant preQuiz
    // rendering for a beat in the middle of transitions it has nothing to do
    // with. The attempt is what this question is actually about, so it's what
    // gets asked.
    //
    // No gate is dodged by any of this. It can only ever KEEP a user on the quiz
    // surface, never grant one: both content gates above have already passed, and
    // every holding condition traces to the server — startQuizStatus is 'pending'
    // only because a session POST the server will itself re-gate is in flight
    // (PriorStepsNotCompleted 409s it), and an attempt exists only because the
    // server handed back a session. Nor is anyone parked here: leaving is an
    // explicit act — quizResultAcknowledged for a pass, startQuizSession for a
    // retake — and both clear what holds it.
    if (state.classroomV2Wizard.startQuizStatus[id] === 'pending') {
      return { kind: 'quiz' }
    }
    // 'fulfilled' holds the phase too, for the beat between the session POST
    // resolving and QuizStep's own effect re-opening it landing in
    // quizAttemptByCourseID below — react to that fulfilled render, dispatch,
    // and its OWN pending render are three separate renders, and progress.quiz
    // still reads whatever it read before this attempt started for every one of
    // them. Without this, that gap reads exactly like "no attempt, quiz not
    // passed" and bounces to preQuiz for a frame — the same flash the comment
    // above describes, just relocated to a different gap. loadQuizAttempt.pending
    // clears startQuizStatus back to 'uninitialized' the moment it hands the
    // hold off to quizAttemptByCourseID, which is what keeps this branch from
    // still being true long after the attempt it was covering for is gone —
    // e.g. after quizResultAcknowledged drops a passed attempt for good.
    if (state.classroomV2Wizard.startQuizStatus[id] === 'fulfilled') {
      return { kind: 'quiz' }
    }
    if (state.classroomV2Wizard.quizAttemptByCourseID[id]) {
      return { kind: 'quiz' }
    }
    // No attempt in hand, so the server's record decides. inProgress means an
    // unfinished session is waiting to be resumed — a cold re-entry mid-attempt
    // lands here and the quiz surface re-opens it.
    if (progress.quiz.kind === 'inProgress') return { kind: 'quiz' }
    if (progress.quiz.kind !== 'passed') return { kind: 'preQuiz' }
    // The survey gate, driven off progress.survey ALONE — deliberately not
    // re-derived from steps/allStepsCompleted/quiz, per the contract. Two of the
    // four cases hold the user here:
    //
    //   notSubmitted — required and open. The normal path.
    //   locked       — required but not open. Unreachable in practice from here
    //                  (both of its prerequisites are gates this selector has
    //                  already passed), but it must NOT fall through: doing so
    //                  would hand a certificate phase to someone whose required
    //                  survey is still outstanding. The survey surface renders
    //                  what's owed instead.
    //
    // notRequired and submitted are the only two that let a user past, and they
    // are the only two the survey can terminate in.
    if (
      progress.survey.kind === 'notSubmitted' ||
      progress.survey.kind === 'locked'
    ) {
      return { kind: 'survey' }
    }
    // Every user who gets this far has earned a certificate, including when the
    // survey was notRequired — issuing it is the API's job, not something the
    // client decides or triggers.
    //
    // The phase carries NOTHING about the certificate's state, deliberately. The
    // API never writes the certificate synchronously: a claim publishes to SNS
    // and a Lambda renders the PDF afterwards, so at the moment a user arrives
    // here the certificate genuinely does not exist yet, and the record will move
    // from pending to issued underneath them. That is a thing to POLL for, not a
    // thing to derive — and the issued record carries a presigned URL that
    // expires in minutes, which has no business being reachable from a phase
    // selector reading a store. The certificate surface asks
    // GET .../certificate itself; this gate's only job is to say the user has
    // earned the right to be standing here.
    return { kind: 'certificate' }
  }

// Whether the stepper should offer the assessment as a live destination.
// Deliberately defined as "what phase would this user be in if they weren't
// reviewing content" rather than by re-testing allStepsCompleted and
// explorationMode itself: the milestone is clickable exactly when dropping the
// review flag would actually move them out of content, so the affordance
// cannot claim a destination selectWizardPhase would refuse to render. Any
// future gate added to selectWizardPhase is inherited here for free.
export let selectIsAssessmentReachable =
  (id: string) => (state: AppRootState) => {
    if (!state.classroomV2Wizard.sessionProgress[id]) return false
    // Both flags this overrides hold the SAME phase for different reasons —
    // deliberate review, and just having finished — and this selector answers
    // one question regardless of which one is currently holding: is there an
    // assessment to advance to at all. Missing either would report a fully
    // finished course as having none, right when the affordance that reads
    // this (the stepper's Final Milestone, the prev/next bar's "Continue to
    // Assessment") most needs to appear.
    let phaseIgnoringReview = selectWizardPhase(id)({
      ...state,
      classroomV2Wizard: {
        ...state.classroomV2Wizard,
        isReviewingContentByCourseID: {
          ...state.classroomV2Wizard.isReviewingContentByCourseID,
          [id]: false,
        },
        justCompletedContentByCourseID: {
          ...state.classroomV2Wizard.justCompletedContentByCourseID,
          [id]: false,
        },
      },
    })
    return (
      phaseIgnoringReview !== null && phaseIgnoringReview.kind !== 'content'
    )
  }

// The full terminal state (steps, quiz, survey, certificate) — not just
// allStepsCompleted — per the design notes above (#3). This is read-only
// derived state consulted by callers that want to react to full completion
// (e.g. defaulting a video step into local review) without dispatching
// setExplorationMode, which would trigger the mode-change middleware's
// progress-clearing side effect. See ComplianceVideoStep/VideoPlayer's
// local review-mode override in course-player.tsx for why that matters:
// forcing the shared explorationModeByCourseID flag on completion would
// wipe the very progress that shows the course is done.
//
// Reads sessionProgress, so it is scoped to the CURRENT restart boundary for
// free: a restarted course's fresh projection reports incomplete steps and a
// notAttempted quiz, and this goes false without knowing restart exists.
export let selectIsCourseFullyComplete =
  (id: string) => (state: AppRootState) => {
    let progress = state.classroomV2Wizard.sessionProgress[id]
    if (!progress) return false
    return (
      progress.allStepsCompleted &&
      progress.quiz.kind === 'passed' &&
      (progress.survey.kind === 'notRequired' ||
        progress.survey.kind === 'submitted') &&
      progress.certificate.kind === 'issued'
    )
  }

export let selectRestartStatusByID = (id: string) => (state: AppRootState) =>
  state.classroomV2Wizard.restartStatus[id] ?? 'uninitialized'

// Whether a restart is underway — from the press until the fresh progress record
// has landed, not just until the POST resolves.
//
// The gap between those two matters more here than in any other write in this
// feature. restartCourse.fulfilled wipes the course's progress, so there is a
// real interval where the POST is done, restartStatus is 'fulfilled', and the
// store holds no progress at all. Anything reading only the request status treats
// that as "restarted, carry on" and renders whatever it shows for a course with
// no progress — which on the overview is the un-started state, flashing the
// learner from "complete" to "not started" and back as the refetch lands.
//
// So this stays true across both halves, and the surfaces disable their trigger
// and hold their message off it.
//
// The second half tests for a progress fetch that hasn't SETTLED, which is not the
// same as one that hasn't succeeded. 'rejected' has to end the restart even though
// no fresh record arrived: the refetch is an ordinary request that can fail on a
// dropped connection, and a wait that only ends on success is a wait that never
// ends — the learner would be left with a permanently disabled restart button and
// a "Restarting…" message for a restart the server completed. Treating it as
// settled hands them back a page they can act on. What they'll see is the wiped
// state, which is honest: the restart really did happen, and the un-started
// overview is what a fresh attempt looks like. A reload re-asks.
//
// Absent counts as unsettled rather than settled, because between the wipe and the
// refetch's own pending action there is a beat where the status is missing
// entirely, and reading that as "done" is what puts the flash back.
let isProgressFetchSettled = (status: Status | undefined) =>
  status === 'fulfilled' || status === 'rejected'

export let selectIsRestartingByID = (id: string) => (state: AppRootState) => {
  let restartStatus = state.classroomV2Wizard.restartStatus[id]
  if (restartStatus === 'pending') return true
  return (
    restartStatus === 'fulfilled' &&
    !isProgressFetchSettled(state.classroomV2Wizard.courseProgressStatus[id])
  )
}

// null means "the certificate surface hasn't started polling yet" — it reads that
// as its cue to dispatch the first poll, mirroring how the quiz and survey
// surfaces treat a null attempt.
export let selectCertificatePollByCourseID =
  (id: string) =>
  (state: AppRootState): CourseV2CertificatePoll | null =>
    state.classroomV2Wizard.certificatePollByCourseID[id] ?? null

export default classroomV2WizardSlice.reducer
